import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { RiShoppingBagLine, RiDeleteBin6Line, RiHeartLine, RiArrowRightUpLine } from 'react-icons/ri';
import './Wishlist.css';
import { useCart } from '../../context/CartContext.js';
import { usePublicSettings } from '../../context/PublicSettingsContext';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import translationMenu from '../../data/translationMenu';
import LanguageContext from '../../context/LanguageContext.js';

const Wishlist = () => {
    const { dir } = useContext(LanguageContext);
    const { t, i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const { wishlistItems, removeFromWishlist, moveToCart } = useCart();
    const { formatPrice } = usePublicSettings();
    const layoutDir = dir();

    return (
        <main className="main">
            <Breadcrumb title={t('wishlist.title')} />

            <section className="wishlist-page">
                <div className="container wishlist-header" dir={layoutDir}>
                    <span className="wishlist-eyebrow">{t('nav.wishlist')}</span>
                    <p className="wishlist-page-desc">{t('wishlist.intro')}</p>
                </div>

                <div className="container wishlist-body" dir={layoutDir}>
                    {wishlistItems.length === 0 ? (
                        <div className="wishlist-empty">
                            <div className="wishlist-empty-visual" aria-hidden="true">
                                <RiHeartLine className="wishlist-empty-heart" />
                            </div>
                            <h2 className="wishlist-empty-title">{t('wishlist.empty')}</h2>
                            <p className="wishlist-empty-text">{t('wishlist.emptyDescription')}</p>
                            <Link to={`/${currentLanguage}/menu`} className="wishlist-btn wishlist-btn--primary">
                                <span>{t('wishlist.browseMenu')}</span>
                                <RiArrowRightUpLine className="wishlist-btn-ico" aria-hidden />
                                <span className="wishlist-btn-shine" aria-hidden />
                            </Link>
                        </div>
                    ) : (
                        <>
                            <p className="wishlist-summary" role="status">
                                {t('wishlist.summary', { count: wishlistItems.length })}
                            </p>
                            <ul className="wishlist-list">
                                {wishlistItems.map((item) => {
                                    const id = item._id || item.id;
                                    const name = item.name[currentLanguage] || item.name.en;
                                    const categoryLabel =
                                        translationMenu[currentLanguage]?.categories?.[item.category] || item.category;
                                    return (
                                        <li key={id}>
                                            <article className="wishlist-card">
                                                <div className="wishlist-card-media">
                                                    <img src={item.imageUrl} alt={name} className="wishlist-card-img" />
                                                </div>
                                                <div className="wishlist-card-main">
                                                    <h2 className="wishlist-card-title">{name}</h2>
                                                    <p className="wishlist-card-category">{categoryLabel}</p>
                                                    <p className="wishlist-card-price">{formatPrice(item.price)}</p>
                                                </div>
                                                <div className="wishlist-card-actions">
                                                    <button
                                                        type="button"
                                                        className="wishlist-btn wishlist-btn--primary wishlist-btn--compact"
                                                        onClick={() => moveToCart(id)}
                                                    >
                                                        <RiShoppingBagLine className="wishlist-btn-lead" aria-hidden />
                                                        <span>{t('wishlist.addToCart')}</span>
                                                        <span className="wishlist-btn-shine" aria-hidden />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="wishlist-btn-remove"
                                                        onClick={() => removeFromWishlist(id)}
                                                        aria-label={t('wishlist.remove')}
                                                    >
                                                        <RiDeleteBin6Line aria-hidden />
                                                    </button>
                                                </div>
                                            </article>
                                        </li>
                                    );
                                })}
                            </ul>
                        </>
                    )}
                </div>
            </section>
        </main>
    );
};

export default Wishlist;
